import { Product, ProductAsset } from '@/services/productService';
import { assetTypeService } from '@/services/assetTypeService';
import type { ProductWithPrice } from '@/hooks/useProductPrice';
import { API_BASE_URL } from '@/config/config';
import { logDebug } from '@/utils/errorHandler';

export type ImageSize = 'thumbnail' | 'medium' | 'full';

/**
 * Helper function to construct proper URLs for media files
 * Handles both proxied requests (development) and direct requests (production)
 *
 * @param url The original URL from the backend
 * @returns The properly constructed URL
 */
function buildMediaUrl(url: string): string {
  // If it's already a full URL or data URL, return as-is
  if (url.startsWith('http') || url.startsWith('data:')) {
    // Special handling for S3 URLs - return them unmodified
    if (url.includes('s3') && url.includes('amazonaws.com')) {
      logDebug('S3 URL detected, returning as-is', { url });
      return url;
    }
    return url;
  }

  // Smart environment detection:
  // - If API_BASE_URL points to a remote server (not localhost), treat as production
  // - This handles the case where frontend runs locally but connects to production backend
  const isRemoteBackend =
    API_BASE_URL && !API_BASE_URL.includes('localhost') && !API_BASE_URL.includes('127.0.0.1');
  const isDev =
    !isRemoteBackend && (import.meta.env?.DEV || process.env.NODE_ENV === 'development');

  // Ensure URL starts with /api/media/ for proper routing to S3ProxyView
  let mediaUrl = url;
  if (!url.startsWith('/api/media/')) {
    // If it's a relative path like "product_images/5/1171/image.webp", add /api/media/ prefix
    if (
      url.startsWith('product_images/') ||
      url.startsWith('upload/') ||
      url.startsWith('media/') ||
      url.startsWith('product_assets/')
    ) {
      mediaUrl = `/api/media/${url}`;
    } else if (
      url.startsWith('/product_images/') ||
      url.startsWith('/upload/') ||
      url.startsWith('/media/') ||
      url.startsWith('/product_assets/')
    ) {
      mediaUrl = `/api/media${url}`;
    } else {
      // For other relative paths, assume they need /api/media/ prefix
      mediaUrl = url.startsWith('/') ? `/api/media${url}` : `/api/media/${url}`;
    }
  }

  // Note: Files are actually stored in product_images/ path in S3, so no path replacement needed

  const result = isDev ? mediaUrl : `${API_BASE_URL}${mediaUrl}`;

  logDebug('Built media URL', {
    originalUrl: url,
    result,
    isDev,
    isRemoteBackend,
    apiBaseUrl: API_BASE_URL,
  });

  return result;
}

/**
 * Consistently selects the primary image URL from a product using a standard priority order
 * Now supports multi-size images with fallbacks
 *
 * @param product The product object containing assets and image fields
 * @param size The desired image size ('thumbnail', 'medium', 'full')
 * @returns The URL of the primary image, or undefined if no suitable image is found
 */
export function pickPrimaryImage(
  product: Product | ProductWithPrice,
  size: ImageSize = 'medium'
): string | undefined {
  // Add debugging for troubleshooting image display issues
  const debug = (message: string, data?: any) => {
    logDebug(`[pickPrimaryImage] ${message}`, data);
  };

  if (!product) {
    debug('No product provided');
    return undefined;
  }

  debug(`Processing product ${product.id} for ${size} image`, {
    productId: product.id,
    assetsLength: product.assets?.length || 0,
    hasThumb: !!product.primary_image_thumb,
    hasAssetUrl: !!product.primary_asset_url,
  });

  // First check if we have a primary asset with multi-size URLs
  if (product.assets?.length) {
    debug(`Checking ${product.assets.length} assets for primary image`);

    const primaryAsset = product.assets.find((a: any) => {
      const isPrimary = a.is_primary;
      const isImage = assetTypeService.isImageAsset(a);
      debug(`Asset ${a.id}: primary=${isPrimary}, isImage=${isImage}`, {
        asset: a,
      });
      return isPrimary && isImage;
    });

    if (primaryAsset) {
      debug('Found primary asset', primaryAsset);
      const url = getImageUrlFromAsset(primaryAsset, size);
      debug(`Primary asset URL: ${url}`);
      return url;
    }

    // If no primary asset, use the first image asset
    const firstImageAsset = product.assets.find((a: any) => {
      const isImage = assetTypeService.isImageAsset(a);
      debug(`First image check - Asset ${a.id}: isImage=${isImage}`, {
        asset: a,
      });
      return isImage;
    });

    if (firstImageAsset) {
      debug('Found first image asset', firstImageAsset);
      const url = getImageUrlFromAsset(firstImageAsset, size);
      debug(`First image asset URL: ${url}`);
      return url;
    }

    debug('No image assets found in assets array');
  } else {
    debug('No assets array found, falling back to legacy fields');
  }

  // Enhanced fallback logic for products without full asset objects (like related products)
  // Use consistent S3 URL handling logic for legacy fields
  const handleLegacyUrl = (rawUrl: string): string => {
    debug(`Processing legacy URL: ${rawUrl}`);

    // S3 detection: URLs with query parameters and absolute paths are likely S3 pre-signed URLs
    const isS3Url =
      rawUrl.includes('?') && (rawUrl.startsWith('https://') || rawUrl.startsWith('http://'));

    if (isS3Url) {
      // For S3 URLs, return directly as they contain pre-signed access
      debug('Detected S3 URL, returning directly');
      return rawUrl;
    }

    // If URL is already a complete HTTP/HTTPS URL without query params, check if it needs fixing
    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
      // Fix common URL formatting issues
      let fixedUrl = rawUrl;

      // Fix http://kernlogic.com:443 -> https://kernlogic.com
      if (rawUrl.includes('kernlogic.com:443')) {
        fixedUrl = rawUrl.replace('http://kernlogic.com:443', 'https://kernlogic.com');
        debug(`Fixed HTTPS port issue: ${rawUrl} -> ${fixedUrl}`);
      }

      // Ensure /api/media/ is in the path for image serving
      if (!fixedUrl.includes('/api/media/') && fixedUrl.includes('/product_images/')) {
        fixedUrl = fixedUrl.replace('/product_images/', '/api/media/product_images/');
        debug(`Added /api/media/ path: ${rawUrl} -> ${fixedUrl}`);
      }

      // Note: Files are actually stored in product_images/ path in S3, so no path replacement needed

      return fixedUrl;
    }

    // For local development URLs, use the media URL builder
    debug('Detected local URL, using buildMediaUrl');
    return buildMediaUrl(rawUrl);
  };

  if (size === 'thumbnail') {
    // Primary preference: dedicated thumbnail field
    if (product.primary_image_thumb) {
      debug(`Using primary_image_thumb: ${product.primary_image_thumb}`);
      return handleLegacyUrl(product.primary_image_thumb);
    }
    // Fallback: use primary_asset_url if no thumb field present (common in list serializer)
    if (product.primary_asset_url) {
      debug(`Fallback to primary_asset_url for thumbnail: ${product.primary_asset_url}`);
      return handleLegacyUrl(product.primary_asset_url);
    }
  }

  if (size === 'medium') {
    // For medium size, prefer primary_asset_url over thumbnail
    if (product.primary_asset_url) {
      debug(`Using primary_asset_url for medium: ${product.primary_asset_url}`);
      return handleLegacyUrl(product.primary_asset_url);
    }
    if (product.primary_image_thumb) {
      debug(`Fallback to primary_image_thumb for medium: ${product.primary_image_thumb}`);
      return handleLegacyUrl(product.primary_image_thumb);
    }
  }

  if (size === 'full') {
    // For full size, primary_asset_url is likely the best option
    if (product.primary_asset_url) {
      debug(`Using primary_asset_url for full: ${product.primary_asset_url}`);
      return handleLegacyUrl(product.primary_asset_url);
    }
    if (product.primary_image_thumb) {
      debug(`Fallback to primary_image_thumb for full: ${product.primary_image_thumb}`);
      return handleLegacyUrl(product.primary_image_thumb);
    }
  }

  // Final fallback - try any available image field
  if (product.primary_asset_url) {
    debug(`Final fallback to primary_asset_url: ${product.primary_asset_url}`);
    return handleLegacyUrl(product.primary_asset_url);
  }

  if (product.primary_image_thumb) {
    debug(`Final fallback to primary_image_thumb: ${product.primary_image_thumb}`);
    return handleLegacyUrl(product.primary_image_thumb);
  }

  debug('No image found for product');
  return undefined;
}

/**
 * Extract the appropriate image URL from an asset based on size preference
 *
 * @param asset The asset object
 * @param size The desired image size
 * @returns The appropriate image URL with fallbacks
 */
export function getImageUrlFromAsset(asset: any, size: ImageSize = 'medium'): string | undefined {
  // Add debugging for troubleshooting image display issues
  const debug = (message: string, data?: any) => {
    logDebug(`[getImageUrlFromAsset] ${message}`, data);
  };

  // Check if asset exists first
  if (!asset) {
    debug('No asset provided');
    return undefined;
  }

  debug(`Processing asset ${asset.id} for ${size} URL`, {
    assetId: asset.id,
    type: asset.type || asset.asset_type || asset.content_type,
    url_thumbnail: asset.url_thumbnail,
    url_medium: asset.url_medium,
    url_full: asset.url_full,
    file_url: asset.file_url,
    file: asset.file,
    url: asset.url,
  });

  // Helper function to handle URL processing with S3 detection
  const processUrl = (rawUrl: string): string | undefined => {
    if (!rawUrl) {
      debug('Empty URL provided to processUrl');
      return undefined;
    }

    debug(`Processing URL: ${rawUrl}`);

    // PRODUCTION S3: Direct S3 URLs should be used as-is
    if (rawUrl.includes('s3') && rawUrl.includes('amazonaws.com')) {
      debug('Detected S3 URL, using directly');
      return rawUrl; // Direct S3 URL - use exactly as provided
    }

    // PRODUCTION S3: If URL is already absolute with query parameters (pre-signed), use it directly
    // This handles S3 storage with correct file paths including Django's random suffixes
    if (rawUrl.includes('?') && (rawUrl.startsWith('https://') || rawUrl.startsWith('http://'))) {
      debug('Detected S3 pre-signed URL, using directly');
      return rawUrl; // Pre-signed S3 URL - use exactly as provided
    }

    // If URL is already a complete HTTP/HTTPS URL without query params, check if it needs fixing
    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
      // Fix common URL formatting issues
      let fixedUrl = rawUrl;

      // Fix http://kernlogic.com:443 -> https://kernlogic.com
      if (rawUrl.includes('kernlogic.com:443')) {
        fixedUrl = rawUrl.replace('http://kernlogic.com:443', 'https://kernlogic.com');
        debug(`Fixed HTTPS port issue: ${rawUrl} -> ${fixedUrl}`);
      }

      // Ensure /api/media/ is in the path for image serving
      if (!fixedUrl.includes('/api/media/') && fixedUrl.includes('/product_images/')) {
        fixedUrl = fixedUrl.replace('/product_images/', '/api/media/product_images/');
        debug(`Added /api/media/ path: ${rawUrl} -> ${fixedUrl}`);
      }

      // Note: Files are actually stored in product_images/ path in S3, so no path replacement needed

      return fixedUrl;
    }

    // DEVELOPMENT/LOCAL: If URL is relative or doesn't have query parameters, use buildMediaUrl
    // This handles local development where files are served directly by Django
    debug('Using buildMediaUrl for local/relative URL');
    return buildMediaUrl(rawUrl);
  };

  // Try size-specific URLs first (from multi-size image processing)
  if (size === 'thumbnail' && asset.url_thumbnail) {
    debug(`Using url_thumbnail: ${asset.url_thumbnail}`);
    return processUrl(asset.url_thumbnail);
  }

  if (size === 'medium' && asset.url_medium) {
    debug(`Using url_medium: ${asset.url_medium}`);
    return processUrl(asset.url_medium);
  }

  if (size === 'full' && asset.url_full) {
    debug(`Using url_full: ${asset.url_full}`);
    return processUrl(asset.url_full);
  }

  // Fallback to main file URL if size-specific URLs aren't available
  const mainUrl = asset.file_url || asset.file || asset.url;
  if (mainUrl) {
    debug(
      `Using fallback URL (${asset.file_url ? 'file_url' : asset.file ? 'file' : 'url'}): ${mainUrl}`
    );
    return processUrl(mainUrl);
  }

  debug('No valid URL found in asset');
  return undefined;
}

/**
 * Get the primary asset object from a product
 *
 * @param product The product object
 * @returns The primary asset object or undefined
 */
export function getPrimaryAsset(product: Product | ProductWithPrice): ProductAsset | undefined {
  if (!product.assets?.length) return undefined;

  // First look for an asset explicitly marked as primary
  const primaryAsset = product.assets.find(
    (a: any) => a.is_primary && assetTypeService.isImageAsset(a)
  );
  if (primaryAsset) return primaryAsset;

  // If no primary asset, use the first image asset
  return product.assets.find((a: any) => assetTypeService.isImageAsset(a));
}

/**
 * Check if a product has any image assets
 *
 * @param product The product object
 * @returns Whether the product has image assets
 */
export function hasImageAssets(product: Product | ProductWithPrice): boolean {
  return !!(
    product.primary_image_thumb ||
    product.primary_asset_url ||
    (product.assets?.length && product.assets.some((a: any) => assetTypeService.isImageAsset(a)))
  );
}

/**
 * Get all available image sizes for a product's primary image
 *
 * @param product The product object
 * @returns Object containing available image URLs for each size
 */
export function getAvailableImageSizes(product: Product | ProductWithPrice) {
  const primaryAsset = getPrimaryAsset(product);

  if (primaryAsset) {
    return {
      thumbnail: getImageUrlFromAsset(primaryAsset, 'thumbnail'),
      medium: getImageUrlFromAsset(primaryAsset, 'medium'),
      full: getImageUrlFromAsset(primaryAsset, 'full'),
    };
  }

  // Fallback to legacy fields
  return {
    thumbnail: product.primary_image_thumb,
    medium: product.primary_asset_url,
    full: product.primary_asset_url,
  };
}
